import React, { useEffect, useRef, useState, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Camera, ImagePlus, RotateCw, Trash2, ChevronLeft, ChevronRight, Loader2, ScanLine } from "lucide-react";
import { useT } from "@/i18n";
import { jsPDF } from "jspdf";

const loadImage = (src) => new Promise((resolve) => { const i = new Image(); i.onload = () => resolve(i); i.src = src; });

// rotation (0/90/180/270) + filter (original/gray/bw) applied to full-res canvas
function processCanvas(img, rotation, filter) {
  const rot = ((rotation % 360) + 360) % 360;
  const swap = rot === 90 || rot === 270;
  const c = document.createElement("canvas");
  c.width = swap ? img.height : img.width;
  c.height = swap ? img.width : img.height;
  const ctx = c.getContext("2d");
  ctx.save();
  ctx.translate(c.width / 2, c.height / 2);
  ctx.rotate((rot * Math.PI) / 180);
  ctx.drawImage(img, -img.width / 2, -img.height / 2);
  ctx.restore();
  if (filter !== "original") {
    const d = ctx.getImageData(0, 0, c.width, c.height);
    const p = d.data;
    for (let i = 0; i < p.length; i += 4) {
      const g = 0.3 * p[i] + 0.59 * p[i + 1] + 0.11 * p[i + 2];
      const v = filter === "bw" ? (g > 135 ? 255 : 0) : g;
      p[i] = p[i + 1] = p[i + 2] = v;
    }
    ctx.putImageData(d, 0, 0);
  }
  return c;
}

// apply crop fractions to a processed canvas
function cropCanvas(pc, crop) {
  const sx = Math.round(crop.x0 * pc.width), sy = Math.round(crop.y0 * pc.height);
  const sw = Math.max(1, Math.round((crop.x1 - crop.x0) * pc.width));
  const sh = Math.max(1, Math.round((crop.y1 - crop.y0) * pc.height));
  const out = document.createElement("canvas");
  out.width = sw; out.height = sh;
  out.getContext("2d").drawImage(pc, sx, sy, sw, sh, 0, 0, sw, sh);
  return out;
}

const FULL_CROP = { x0: 0, y0: 0, x1: 1, y1: 1 };

export default function DocumentScanner({ open, onClose, onSend }) {
  const { t } = useT();
  const [pages, setPages] = useState([]);
  const [cur, setCur] = useState(0);
  const [format, setFormat] = useState("pdf");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [dispSize, setDispSize] = useState({ w: 0, h: 0 });
  const camRef = useRef();
  const impRef = useRef();
  const canvasRef = useRef();
  const wrapRef = useRef();
  const drag = useRef(null);

  useEffect(() => {
    if (open) { setPages([]); setCur(0); setFormat("pdf"); setName(`scan-${new Date().toISOString().slice(0, 16).replace(/[:T]/g, "")}`); }
  }, [open]);

  const page = pages[cur];

  const addFiles = async (list) => {
    const files = Array.from(list || []).filter((f) => f.type.startsWith("image/"));
    if (!files.length) return;
    const read = (f) => new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(f); });
    const newPages = [];
    for (const f of files) newPages.push({ src: await read(f), rotation: 0, filter: "original", crop: { ...FULL_CROP } });
    setPages((p) => { const next = [...p, ...newPages]; setCur(next.length - 1); return next; });
  };

  const renderDisplay = useCallback(async () => {
    if (!page || !canvasRef.current) return;
    const img = await loadImage(page.src);
    const pc = processCanvas(img, page.rotation, page.filter);
    const maxW = Math.min(wrapRef.current?.clientWidth || 460, 460);
    const maxH = 420;
    let scale = Math.min(maxW / pc.width, maxH / pc.height, 1);
    const w = Math.round(pc.width * scale), h = Math.round(pc.height * scale);
    const c = canvasRef.current;
    c.width = w; c.height = h;
    c.getContext("2d").drawImage(pc, 0, 0, w, h);
    setDispSize({ w, h });
  }, [page]);

  useEffect(() => { renderDisplay(); }, [renderDisplay]);

  const updateCrop = (patch) => setPages((p) => p.map((pg, i) => i === cur ? { ...pg, crop: { ...pg.crop, ...patch } } : pg));
  const rotate = () => setPages((p) => p.map((pg, i) => i === cur ? { ...pg, rotation: (pg.rotation + 90) % 360 } : pg));
  const setFilter = (f) => setPages((p) => p.map((pg, i) => i === cur ? { ...pg, filter: f } : pg));
  const deletePage = () => setPages((p) => { const next = p.filter((_, i) => i !== cur); setCur((c) => Math.max(0, Math.min(c, next.length - 1))); return next; });

  const startDrag = (corner, e) => {
    e.preventDefault(); e.stopPropagation();
    drag.current = { corner };
  };
  useEffect(() => {
    const move = (e) => {
      if (!drag.current || !canvasRef.current) return;
      const rect = canvasRef.current.getBoundingClientRect();
      const cx = e.touches ? e.touches[0].clientX : e.clientX;
      const cy = e.touches ? e.touches[0].clientY : e.clientY;
      let fx = Math.min(1, Math.max(0, (cx - rect.left) / rect.width));
      let fy = Math.min(1, Math.max(0, (cy - rect.top) / rect.height));
      const c = drag.current.corner;
      const cr = pages[cur]?.crop; if (!cr) return;
      const gap = 0.08;
      if (c === "tl") updateCrop({ x0: Math.min(fx, cr.x1 - gap), y0: Math.min(fy, cr.y1 - gap) });
      if (c === "tr") updateCrop({ x1: Math.max(fx, cr.x0 + gap), y0: Math.min(fy, cr.y1 - gap) });
      if (c === "bl") updateCrop({ x0: Math.min(fx, cr.x1 - gap), y1: Math.max(fy, cr.y0 + gap) });
      if (c === "br") updateCrop({ x1: Math.max(fx, cr.x0 + gap), y1: Math.max(fy, cr.y0 + gap) });
    };
    const up = () => { drag.current = null; };
    window.addEventListener("mousemove", move); window.addEventListener("mouseup", up);
    window.addEventListener("touchmove", move, { passive: false }); window.addEventListener("touchend", up);
    return () => { window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up); window.removeEventListener("touchmove", move); window.removeEventListener("touchend", up); };
  }, [pages, cur]); // eslint-disable-line

  const buildFinal = async (pg) => {
    const img = await loadImage(pg.src);
    const pc = processCanvas(img, pg.rotation, pg.filter);
    return cropCanvas(pc, pg.crop);
  };

  const send = async () => {
    if (!pages.length) return;
    setBusy(true);
    try {
      let file;
      const fname = (name || "scan").replace(/[^a-zA-Z0-9-_]/g, "_");
      if (format === "pdf" || pages.length > 1) {
        const first = await buildFinal(pages[0]);
        const pdf = new jsPDF({ orientation: first.width >= first.height ? "landscape" : "portrait", unit: "px", format: [first.width, first.height] });
        for (let i = 0; i < pages.length; i++) {
          const canvas = i === 0 ? first : await buildFinal(pages[i]);
          const data = canvas.toDataURL("image/jpeg", 0.85);
          if (i > 0) pdf.addPage([canvas.width, canvas.height], canvas.width >= canvas.height ? "landscape" : "portrait");
          pdf.addImage(data, "JPEG", 0, 0, canvas.width, canvas.height);
        }
        const blob = pdf.output("blob");
        file = new File([blob], `${fname}.pdf`, { type: "application/pdf" });
      } else {
        const canvas = await buildFinal(pages[0]);
        const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.9));
        file = new File([blob], `${fname}.jpg`, { type: "image/jpeg" });
      }
      await onSend(file);
      onClose();
    } finally { setBusy(false); }
  };

  const cr = page?.crop || FULL_CROP;
  const handle = (corner, left, top) => (
    <div onMouseDown={(e) => startDrag(corner, e)} onTouchStart={(e) => startDrag(corner, e)}
      data-testid={`crop-${corner}`}
      style={{ left: `${left * 100}%`, top: `${top * 100}%` }}
      className="absolute w-5 h-5 -ml-2.5 -mt-2.5 rounded-full bg-primary border-2 border-white shadow cursor-move touch-none z-20" />
  );

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ScanLine className="w-5 h-5 text-primary" />{t("scanDocument")}</DialogTitle>
        </DialogHeader>

        <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
        <input ref={impRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />

        {pages.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 gap-4 text-center">
            <ScanLine className="w-12 h-12 text-muted-foreground opacity-40" />
            <p className="text-sm text-muted-foreground">{t("scanNoPages")}</p>
            <div className="flex gap-2">
              <Button onClick={() => camRef.current?.click()} data-testid="scan-camera"><Camera className="w-4 h-4 mr-2" />{t("scanCamera")}</Button>
              <Button variant="outline" onClick={() => impRef.current?.click()} data-testid="scan-import"><ImagePlus className="w-4 h-4 mr-2" />{t("scanImport")}</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {/* editor */}
            <div ref={wrapRef} className="relative flex items-center justify-center bg-muted/40 rounded-lg p-2 min-h-[300px]">
              <div className="relative" style={{ width: dispSize.w, height: dispSize.h }}>
                <canvas ref={canvasRef} className="rounded shadow max-w-full" data-testid="scan-canvas" />
                {/* crop overlay */}
                {dispSize.w > 0 && (
                  <>
                    <div className="absolute border-2 border-primary/80 pointer-events-none z-10"
                      style={{ left: `${cr.x0 * 100}%`, top: `${cr.y0 * 100}%`, width: `${(cr.x1 - cr.x0) * 100}%`, height: `${(cr.y1 - cr.y0) * 100}%` }} />
                    {handle("tl", cr.x0, cr.y0)}
                    {handle("tr", cr.x1, cr.y0)}
                    {handle("bl", cr.x0, cr.y1)}
                    {handle("br", cr.x1, cr.y1)}
                  </>
                )}
              </div>
            </div>

            {/* tools */}
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex rounded-lg border border-border overflow-hidden">
                {[["original", t("scanColor")], ["gray", t("scanGray")], ["bw", t("scanBw")]].map(([f, lbl]) => (
                  <button key={f} onClick={() => setFilter(f)} data-testid={`scan-filter-${f}`}
                    className={`px-3 py-1.5 text-xs font-medium ${page.filter === f ? "bg-primary text-primary-foreground" : "hover:bg-accent"}`}>{lbl}</button>
                ))}
              </div>
              <Button size="sm" variant="outline" onClick={rotate} data-testid="scan-rotate"><RotateCw className="w-4 h-4" /></Button>
              <Button size="sm" variant="outline" onClick={() => updateCrop({ ...FULL_CROP })} data-testid="scan-reset-crop">{t("scanResetCrop")}</Button>
              <Button size="sm" variant="ghost" className="text-destructive" onClick={deletePage} data-testid="scan-delete-page"><Trash2 className="w-4 h-4" /></Button>
            </div>

            {/* pages nav */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Button size="icon" variant="outline" disabled={cur === 0} onClick={() => setCur((c) => c - 1)} data-testid="scan-prev-page"><ChevronLeft className="w-4 h-4" /></Button>
                <span className="text-sm text-muted-foreground">{t("scanPage")} {cur + 1} / {pages.length}</span>
                <Button size="icon" variant="outline" disabled={cur === pages.length - 1} onClick={() => setCur((c) => c + 1)} data-testid="scan-next-page"><ChevronRight className="w-4 h-4" /></Button>
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" onClick={() => camRef.current?.click()} data-testid="scan-add-camera"><Camera className="w-4 h-4" /></Button>
                <Button size="sm" variant="outline" onClick={() => impRef.current?.click()} data-testid="scan-add-import"><ImagePlus className="w-4 h-4" /></Button>
              </div>
            </div>

            {/* export */}
            <div className="flex flex-wrap items-center gap-2 border-t border-border pt-3">
              <Input value={name} onChange={(e) => setName(e.target.value)} className="h-9 flex-1 min-w-[140px]" data-testid="scan-name" />
              <div className="flex rounded-lg border border-border overflow-hidden">
                {[["pdf", "PDF"], ["jpeg", "JPEG"]].map(([f, lbl]) => (
                  <button key={f} onClick={() => setFormat(f)} disabled={f === "jpeg" && pages.length > 1} data-testid={`scan-format-${f}`}
                    className={`px-3 py-1.5 text-xs font-medium disabled:opacity-40 ${format === f ? "bg-primary text-primary-foreground" : "hover:bg-accent"}`}>{lbl}</button>
                ))}
              </div>
              <Button onClick={send} disabled={busy} data-testid="scan-send">
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ScanLine className="w-4 h-4 mr-2" />}{t("send")}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
